import React, { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const PortalDateRangePicker = ({ 
  dateRange, 
  onDateRangeChange, 
  variant = 'dark', // 'dark' for search bar, 'light' for home page, 'quickbook' for new home
  className = '',
  primaryColor = '#f97316' // Default orange
}) => {
  const [open, setOpen] = useState(false);
  const [selectingStep, setSelectingStep] = useState('checkin'); // 'checkin' or 'checkout'

  useEffect(() => {
    if (dateRange?.from && !dateRange?.to) {
      setSelectingStep('checkout');
    } else if (!dateRange?.from) {
      setSelectingStep('checkin');
    }
  }, [dateRange]);

  const handleSelect = (range) => {
    if (!range) {
      // Reset
      onDateRangeChange(undefined);
      setSelectingStep('checkin');
      return;
    }

    if (!range.from) {
      onDateRangeChange(undefined);
      setSelectingStep('checkin');
      return;
    }

    // If we have both from and to, we're done
    if (range.from && range.to) {
      onDateRangeChange(range);
      setSelectingStep('checkin');
      setTimeout(() => setOpen(false), 200);
      return;
    }

    // Only from selected, waiting for to
    onDateRangeChange(range);
    setSelectingStep('checkout');
  };

  const formatDateShort = (date) => {
    if (!date) return null;
    return format(date, 'dd MMM yyyy');
  };

  const formatDateLong = (date) => {
    if (!date) return null;
    return format(date, 'EEE, dd MMM yyyy');
  };

  const getDayName = (date) => {
    if (!date) return '';
    return format(date, 'EEEE');
  };

  const getDateNum = (date) => {
    if (!date) return '';
    return format(date, 'd');
  };

  const getMonthYear = (date) => {
    if (!date) return '';
    return format(date, "MMM''yy");
  };

  const calculateNights = () => {
    if (dateRange?.from && dateRange?.to) {
      return Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const nights = calculateNights();

  if (variant === 'dark') {
    // Dark variant for the sticky search bar
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button 
            className={`flex items-stretch cursor-pointer hover:bg-white/5 transition-colors ${className}`}
            data-testid="date-range-picker"
          >
            {/* Check-in */}
            <div className="flex flex-col justify-center px-4 border-r border-white/10 min-w-[160px]">
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">Check-In</span>
              {dateRange?.from ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold text-white">{getDateNum(dateRange.from)}</span>
                  <span className="text-xs text-white/80">{getMonthYear(dateRange.from)}</span>
                  <span className="text-[10px] text-white/50">{getDayName(dateRange.from)}</span>
                </div>
              ) : (
                <span className="text-sm font-medium text-white/60">Select Date</span>
              )}
            </div>
            {/* Nights badge */}
            {nights > 0 && (
              <div className="flex items-center px-2">
                <div className="bg-white/10 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {nights}N
                </div>
              </div>
            )}
            {/* Check-out */}
            <div className="flex flex-col justify-center px-4 border-r border-white/10 min-w-[160px]">
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">Check-Out</span>
              {dateRange?.to ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold text-white">{getDateNum(dateRange.to)}</span>
                  <span className="text-xs text-white/80">{getMonthYear(dateRange.to)}</span>
                  <span className="text-[10px] text-white/50">{getDayName(dateRange.to)}</span>
                </div>
              ) : (
                <span className="text-sm font-medium text-white/60">Select Date</span>
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-2xl border-0 rounded-xl" align="start" sideOffset={8}>
          <CalendarBody 
            dateRange={dateRange} 
            selectingStep={selectingStep} 
            onSelect={handleSelect} 
            nights={nights}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Light variant for the home page search box
  if (variant === 'light') {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button 
            className={`flex items-stretch cursor-pointer w-full hover:bg-gray-50 rounded-lg transition-colors border border-gray-200 ${className}`}
            data-testid="date-range-picker"
          >
            {/* Check-in */}
            <div className="flex-1 flex flex-col justify-center px-4 py-2.5 border-r border-gray-200">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">Check-In</span>
              {dateRange?.from ? (
                <div className="flex items-baseline gap-1.5 text-left">
                  <span className="text-lg font-bold text-gray-900">{getDateNum(dateRange.from)}</span>
                  <span className="text-xs text-gray-600">{getMonthYear(dateRange.from)}</span>
                  <span className="text-[10px] text-gray-400">{getDayName(dateRange.from)}</span>
                </div>
              ) : (
                <span className="text-sm text-gray-400 text-left">Select Date</span>
              )}
            </div>
            {/* Nights */}
            {nights > 0 && (
              <div className="flex items-center px-2">
                <div className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {nights}N
                </div>
              </div>
            )}
            {/* Check-out */}
            <div className="flex-1 flex flex-col justify-center px-4 py-2.5">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">Check-Out</span>
              {dateRange?.to ? (
                <div className="flex items-baseline gap-1.5 text-left">
                  <span className="text-lg font-bold text-gray-900">{getDateNum(dateRange.to)}</span>
                  <span className="text-xs text-gray-600">{getMonthYear(dateRange.to)}</span>
                  <span className="text-[10px] text-gray-400">{getDayName(dateRange.to)}</span>
                </div>
              ) : (
                <span className="text-sm text-gray-400 text-left">Select Date</span>
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-2xl border-0 rounded-xl" align="center" sideOffset={8}>
          <CalendarBody 
            dateRange={dateRange} 
            selectingStep={selectingStep} 
            onSelect={handleSelect} 
            nights={nights}
            primaryColor={primaryColor}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Quick Book variant - modern rounded style for new home page
  if (variant === 'quickbook') {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button 
            className={`flex items-center gap-3 cursor-pointer w-full ${className}`}
            data-testid="date-range-picker"
          >
            <svg className="h-5 w-5 flex-shrink-0" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="flex-1 text-left">
              <label className="text-xs text-gray-500 font-medium block mb-0.5">Check-in — Check-out</label>
              {dateRange?.from ? (
                <div className="text-base font-semibold text-gray-900">
                  {format(dateRange.from, 'd MMM')} — {dateRange?.to ? format(dateRange.to, 'd MMM') : 'Select'}
                  {nights > 0 && (
                    <span className="ml-2 text-xs font-normal text-gray-500">({nights} night{nights > 1 ? 's' : ''})</span>
                  )}
                </div>
              ) : (
                <div className="text-base font-semibold text-gray-400">Select dates</div>
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-2xl border-0 rounded-xl" align="center" sideOffset={8}>
          <CalendarBody 
            dateRange={dateRange} 
            selectingStep={selectingStep} 
            onSelect={handleSelect} 
            nights={nights}
            primaryColor={primaryColor}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Booking.com variant - clean minimal style
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className={`flex items-center gap-3 cursor-pointer w-full ${className}`}
          data-testid="date-range-picker"
        >
          <svg className="h-6 w-6 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div className="flex-1 text-left">
            {dateRange?.from ? (
              <div className="text-base font-medium text-gray-900">
                {format(dateRange.from, 'EEE d MMM')} — {dateRange?.to ? format(dateRange.to, 'EEE d MMM') : 'Check-out'}
              </div>
            ) : (
              <div className="text-base font-medium text-gray-400">Check-in — Check-out</div>
            )}
            {nights > 0 && (
              <span className="text-xs text-gray-500">{nights} night{nights > 1 ? 's' : ''}</span>
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 shadow-2xl border-0 rounded-xl" align="center" sideOffset={8}>
        <CalendarBody 
          dateRange={dateRange} 
          selectingStep={selectingStep} 
          onSelect={handleSelect} 
          nights={nights}
          primaryColor={primaryColor}
        />
      </PopoverContent>
    </Popover>
  );
};

// Shared calendar body for both variants
const CalendarBody = ({ dateRange, selectingStep, onSelect, nights, primaryColor = '#f97316' }) => {
  const formatDisplay = (date) => {
    if (!date) return 'Select date';
    return format(date, 'EEE, dd MMM yyyy');
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      {/* Header showing check-in / check-out */}
      <div className="px-5 py-3 bg-gray-50 border-b">
        <div className="flex items-center gap-4">
          <div className={`flex-1 p-2 rounded-lg ${selectingStep === 'checkin' ? 'ring-2' : ''}`}
            style={selectingStep === 'checkin' ? { backgroundColor: primaryColor + '10', ringColor: primaryColor } : {}}
          >
            <div className="text-[10px] font-bold text-gray-500 uppercase">Check-in</div>
            <div className={`text-sm font-bold ${dateRange?.from ? 'text-gray-900' : 'text-gray-400'}`}>
              {formatDisplay(dateRange?.from)}
            </div>
            {selectingStep === 'checkin' && !dateRange?.from && (
              <div className="text-[10px] font-medium mt-0.5" style={{ color: primaryColor }}>Click a date below</div>
            )}
          </div>
          
          <div className="flex flex-col items-center">
            {nights > 0 ? (
              <Badge className="text-white text-[10px] px-2" style={{ backgroundColor: primaryColor }}>
                {nights} {nights === 1 ? 'Night' : 'Nights'}
              </Badge>
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </div>
          
          <div className={`flex-1 p-2 rounded-lg ${selectingStep === 'checkout' ? 'ring-2' : ''}`}
            style={selectingStep === 'checkout' ? { backgroundColor: primaryColor + '10', ringColor: primaryColor } : {}}
          >
            <div className="text-[10px] font-bold text-gray-500 uppercase">Check-out</div>
            <div className={`text-sm font-bold ${dateRange?.to ? 'text-gray-900' : 'text-gray-400'}`}>
              {formatDisplay(dateRange?.to)}
            </div>
            {selectingStep === 'checkout' && !dateRange?.to && (
              <div className="text-[10px] font-medium mt-0.5" style={{ color: primaryColor }}>Now select check-out</div>
            )}
          </div>
        </div>
      </div>
      
      {/* Calendar */}
      <Calendar
        mode="range"
        selected={dateRange}
        onSelect={onSelect}
        numberOfMonths={2}
        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
        className="p-3"
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-sm font-semibold",
          nav: "space-x-1 flex items-center",
          nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50",
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell: "text-gray-500 rounded-md w-9 font-medium text-[0.75rem]",
          row: "flex w-full mt-1",
          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-orange-50 [&:has([aria-selected].day-outside)]:bg-orange-50/50 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
          day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-orange-100 inline-flex items-center justify-center",
          day_range_start: "day-range-start bg-orange-500 text-white hover:bg-orange-600 rounded-l-md",
          day_range_end: "day-range-end bg-orange-500 text-white hover:bg-orange-600 rounded-r-md",
          day_selected: "bg-orange-500 text-white hover:bg-orange-600 focus:bg-orange-500 focus:text-white",
          day_today: "bg-orange-100 text-orange-900 font-bold",
          day_outside: "day-outside text-gray-300 aria-selected:bg-orange-50/50 aria-selected:text-gray-400",
          day_disabled: "text-gray-300",
          day_range_middle: "aria-selected:bg-orange-50 aria-selected:text-orange-900",
          day_hidden: "invisible",
        }}
      />
    </div>
  );
};

export default PortalDateRangePicker;
